import { useEffect, useRef, type RefObject } from "react";

import type { UseTrackVisibilityOptions } from "./types";
import { generateTrackingId } from "./utils";
import { useVisibilityTrackerContext } from "./VisibilityTrackerProvider";

/**
 * Hook for tracking component visibility in the viewport
 * @param componentRef - Ref to the component to track
 * @param options - Visibility tracking options
 * @returns Object with visibility tracking utilities
 */
export function useTrackVisibility<T = any>(
  componentRef: RefObject<T>,
  options: UseTrackVisibilityOptions = {},
) {
  // Default once to true for better performance
  const optionsWithDefaults = {
    once: true,
    ...options,
  };
  const context = useVisibilityTrackerContext();
  const trackingIdRef = useRef<string>(generateTrackingId());
  const optionsRef = useRef(optionsWithDefaults);

  // Update options ref when options change (this allows the tracker to always use latest options)
  useEffect(() => {
    optionsRef.current = optionsWithDefaults;
  });

  // Register component with the visibility tracker (only on mount)
  useEffect(() => {
    const trackingId = trackingIdRef.current;
    const { registerComponent, unregisterComponent } = context;

    if (componentRef.current) {
      registerComponent(trackingId, componentRef, optionsRef.current);
    }

    return () => {
      unregisterComponent(trackingId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount/unmount - context functions are stable

  // Return utilities for manual visibility checking
  return {
    /**
     * Manually check visibility for this component
     */
    checkVisibility: () => {
      context.checkVisibility(trackingIdRef.current);
    },

    /**
     * Get the tracking ID for this component
     */
    trackingId: trackingIdRef.current,
  };
}
